import { useCallback, useState } from 'react';
import { CloseIcon, WarningIcon } from './icons';
import { AmberBanner } from './layout/StatusBanner';
import { isPaperThemeActive } from '@/hooks/useThemeFlag';

interface EphemeralSessionBannerProps {
  visible: boolean;
  onLogin: () => void;
}

export function EphemeralSessionBanner({
  visible,
  onLogin,
}: EphemeralSessionBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  const handleDismiss = useCallback(() => setDismissed(true), []);

  if (!visible || dismissed) return null;

  if (isPaperThemeActive()) {
    return (
      <div className="max-w-xs animate-in fade-in slide-in-from-top-2 duration-300">
        <div className="flex items-start gap-2.5 rounded-xl border border-[var(--hair)] bg-[var(--surface)] px-3.5 py-2.5 text-xs text-[var(--fg-2)] shadow-lg backdrop-blur-sm">
          <WarningIcon className="mt-px h-4 w-4 shrink-0 text-[var(--fg-3)]" />
          <div className="flex-1 leading-relaxed">
            <span>
              You&apos;re not signed in. This session is temporary and will be
              lost if you close the page.{' '}
            </span>
            <button
              onClick={onLogin}
              className="inline font-medium text-[var(--periwinkle)] underline underline-offset-2 hover:text-[var(--fg)] transition-colors"
            >
              Sign in
            </button>
            <span> to keep it.</span>
          </div>
          <button
            onClick={handleDismiss}
            className="mt-px shrink-0 rounded p-0.5 text-[var(--fg-4)] hover:text-[var(--fg-2)] transition-colors"
            aria-label="Dismiss"
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xs animate-in fade-in slide-in-from-top-2 duration-300">
      <AmberBanner className="items-start gap-2.5 rounded-lg bg-amber-950/60 px-3.5 py-2.5 text-xs text-amber-200/90 shadow-lg backdrop-blur-sm">
        <WarningIcon className="mt-px h-4 w-4 shrink-0 text-amber-400/80" />
        <div className="flex-1 leading-relaxed">
          <span>
            You&apos;re not signed in. This session is temporary and will be
            lost if you close the page.{' '}
          </span>
          <button
            onClick={onLogin}
            className="inline font-medium text-amber-300 underline underline-offset-2 hover:text-amber-100 transition-colors"
          >
            Sign in
          </button>
          <span> to keep it.</span>
        </div>
        <button
          onClick={handleDismiss}
          className="mt-px shrink-0 rounded p-0.5 text-amber-400/60 hover:text-amber-200 transition-colors"
          aria-label="Dismiss"
        >
          <CloseIcon className="h-3.5 w-3.5" />
        </button>
      </AmberBanner>
    </div>
  );
}
